import { FC, useState } from 'react';

import { theme } from '../../theme';
import Tooltip from '../Tooltip';
import Typography from '../Typography';
import {
  StyledContainer,
  StyledControl,
  StyledIndicator,
  StyledMarksRow,
  StyledRoot,
  StyledRow,
  StyledThumb,
  StyledTick,
  StyledTicksRow,
  StyledTrack,
} from './Slider.styled';
import type { SliderProps } from './types';

const Slider: FC<SliderProps> = ({
  defaultValue,
  value,
  onValueChange,
  min = 0,
  orientation = 'horizontal',
  size = 'md',
  colorPalette = 'primary',
  label,
  tooltip,
  startAdornment,
  endAdornment,
  marks,
  showMarkTicks,
  ...rest
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(value ?? defaultValue ?? min);

  const currentValue = value ?? uncontrolledValue;
  const values = Array.isArray(currentValue) ? currentValue : [currentValue];
  const isRange = values.length > 1;

  const handleValueChange: SliderProps['onValueChange'] = (newValue, eventDetails) => {
    setUncontrolledValue(newValue);
    onValueChange?.(newValue, eventDetails);
  };

  const renderThumb = (index?: number) => {
    const thumb = <StyledThumb index={index} $colorPalette={colorPalette} />;

    if (!tooltip) return thumb;

    const thumbValue = values[index ?? 0];
    const tooltipLabel =
      typeof tooltip === 'function' ? tooltip(thumbValue, index ?? 0) : thumbValue;

    return (
      <Tooltip title={tooltipLabel} side="top" arrow>
        {thumb}
      </Tooltip>
    );
  };

  return (
    <StyledContainer $orientation={orientation}>
      {label && (
        <Typography variant="body3" fontWeight={500}>
          {label}
        </Typography>
      )}
      <StyledRow $orientation={orientation}>
        {startAdornment}
        <StyledRoot
          defaultValue={defaultValue}
          value={value}
          onValueChange={handleValueChange}
          min={min}
          orientation={orientation}
          {...rest}
        >
          <StyledControl>
            <StyledTrack $size={size}>
              <StyledIndicator $colorPalette={colorPalette} />
              {isRange ? (
                <>
                  {renderThumb(0)}
                  {renderThumb(1)}
                </>
              ) : (
                renderThumb()
              )}
            </StyledTrack>
          </StyledControl>
        </StyledRoot>
        {endAdornment}
      </StyledRow>
      {!!marks?.length && (
        <>
          {showMarkTicks && (
            <StyledTicksRow>
              {marks.map((_, index) => (
                <StyledTick key={index} />
              ))}
            </StyledTicksRow>
          )}
          <StyledMarksRow>
            {marks.map((mark, index) => (
              <Typography key={index} variant="body3" color={theme.colors.text.secondary}>
                {mark}
              </Typography>
            ))}
          </StyledMarksRow>
        </>
      )}
    </StyledContainer>
  );
};

export default Slider;

Slider.displayName = 'Slider';
